import { z } from 'zod';
import { createBusinessContext } from '../BusinessContext.js';
import { BusinessContextSchema, RowIDSchema } from './BusinessAnalysisSchemas.js';
import { ImportRefSchema } from './ReportSpecificationSchemas.js';
export const BusinessConfigurationItemSchema = z.object({
    businessContext: z.string().min(1).transform(createBusinessContext),
    companyName: z.string().min(1),
});
export const BusinessConfigurationsResponseSchema = z.object({
    businessConfigurations: z.array(BusinessConfigurationItemSchema),
    deployEnv: z.enum(['staging', 'production', 'development', 'test']),
});
// Xero Configuration Schemas
const XeroCustomConnectionCredentialsConfigSchema = z.object({
    authType: z.literal('custom-connection'),
    credentialsPrefix: z.string().min(1),
});
export const XeroCredentialsConfigSchema = z.discriminatedUnion('authType', [
    XeroCustomConnectionCredentialsConfigSchema,
]);
export const XeroConfigurationSchema = z.object({
    credentials: XeroCredentialsConfigSchema,
});
// Karbon Configuration Schema
export const KarbonConfigurationSchema = z.object({
    credentialsPrefix: z.string().min(1),
});
// Files Configuration Schema
export const FilesConfigurationSchema = z.object({
    provider: z.string().min(1),
});
// Ledger Configuration Schemas
const XeroLedgerConfigurationSchema = z.object({
    type: z.literal('xero'),
});
const XlsxLedgerConfigurationSchema = z.object({
    type: z.literal('xlsx'),
    rawPath: z.string().min(1),
    parsedPath: z.string().min(1),
    defaultCurrency: z.string().min(1),
});
export const LedgerConfigurationSchema = z.discriminatedUnion('type', [
    XeroLedgerConfigurationSchema,
    XlsxLedgerConfigurationSchema,
]);
export const MicrosoftGraphConfigurationSchema = z.object({
    credentialsPrefix: z.string().min(1),
    sharepointSiteId: z.string().min(1).optional(),
    sharepointDriveId: z.string().min(1).optional(),
});
// Client Storage Configuration Schema
export const ClientStorageProviderTypeSchema = z.enum(['SHAREPOINT', 'GCS', 'GOOGLE_DRIVE']);
export const ClientStorageConfigurationSchema = z.object({
    enabledProviders: z.array(ClientStorageProviderTypeSchema),
    clientFolderRoot: z.string().optional(),
});
// GCS Configuration Schema
export const GCSConfigurationSchema = z
    .object({
    bucketName: z.string().min(1),
})
    .optional();
// Business Context Children Schema
// Used by /api/business-context-children endpoint for cross-entity validation
export const BusinessContextChildEntitiesSchema = z.record(BusinessContextSchema, z.object({
    childEntities: z.array(BusinessContextSchema).default([]),
}));
// Full Business Configuration Schema
export const FullBusinessConfigurationSchema = z.object({
    companyName: z.string().min(1),
    accountMetadataKey: z.string().min(1),
    childEntities: z.array(z.string().min(1)).default([]),
    xero: XeroConfigurationSchema.optional(),
    karbon: KarbonConfigurationSchema.optional(),
    files: FilesConfigurationSchema.optional(),
    ledger: LedgerConfigurationSchema.optional(),
    microsoftGraph: MicrosoftGraphConfigurationSchema.optional(),
    gcs: GCSConfigurationSchema,
    clientStorage: ClientStorageConfigurationSchema.optional(),
});
export const BusinessConfigurationResponseSchema = z.object({
    businessConfiguration: FullBusinessConfigurationSchema,
});
// Dependee Expectations Schema
// Used by /api/dependee-expectations endpoint for cross-entity validation
// Maps ImportRef (dependee spec) → array of RowIDs that dependee expects from target
export const DependeeExpectationsResponseSchema = z.object({
    expectations: z.record(ImportRefSchema, z.array(RowIDSchema)),
});
//# sourceMappingURL=BusinessConfigurationSchemas.js.map